/**
 * Betreuungs-Wikipedia - Wissensdatenbank & Lern-System
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Plus, Search, Edit, Trash2, ThumbsUp,
  Sparkles, Brain, Lightbulb, MessageSquare, Send, Loader2,
  CheckCircle, Settings, BarChart3, Zap, ChevronRight, X, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
// Dialog imports removed - forms are now inline
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import {
  Card, CardContent, CardHeader, CardTitle
} from '@/components/ui/card';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const KATEGORIEN = [
  { value: 'allgemein', label: 'Allgemein', color: 'bg-slate-100 text-slate-700' },
  { value: 'schriftstueck', label: 'Schriftstücke', color: 'bg-blue-100 text-blue-700' },
  { value: 'behoerde', label: 'Behörden', color: 'bg-green-100 text-green-700' },
  { value: 'verfahren', label: 'Verfahren', color: 'bg-purple-100 text-purple-700' },
  { value: 'muster', label: 'Muster & Vorlagen', color: 'bg-amber-100 text-amber-700' },
  { value: 'roadmap', label: 'Roadmap', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'technik', label: 'Technik', color: 'bg-teal-100 text-teal-700' },
];

export default function WikiPage() {
  const [eintraege, setEintraege] = useState([]);
  const [regeln, setRegeln] = useState([]);
  const [statistik, setStatistik] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKategorie, setSelectedKategorie] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  
  // Dialog States
  const [editDialog, setEditDialog] = useState({ open: false, eintrag: null });
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Daten laden
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [eintraegeRes, regelnRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/wiki/eintraege?${selectedKategorie ? `kategorie=${selectedKategorie}` : ''}&${searchTerm ? `suche=${searchTerm}` : ''}`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/wiki/regeln`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/wiki/lernen/statistik`).then(r => r.json())
      ]);
      setEintraege(Array.isArray(eintraegeRes) ? eintraegeRes : []);
      setRegeln(Array.isArray(regelnRes) ? regelnRes : []);
      setStatistik(statsRes);
    } catch (e) {
      console.error('Fehler:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedKategorie, searchTerm]);

  useEffect(() => { loadData(); }, [loadData]);

  // Eintrag speichern
  const saveEintrag = async (data) => {
    try {
      const isNew = !data.id;
      const url = isNew 
        ? `${BACKEND_URL}/api/wiki/eintraege`
        : `${BACKEND_URL}/api/wiki/eintraege/${data.id}`;
      
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        toast.success(isNew ? 'Eintrag erstellt' : 'Eintrag aktualisiert');
        setEditDialog({ open: false, eintrag: null });
        loadData();
      }
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Eintrag löschen
  const deleteEintrag = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/wiki/eintraege/${id}`, { method: 'DELETE' });
      toast.success('Gelöscht');
      loadData();
    } catch (e) {
      toast.error('Fehler');
    }
  };

  // Als hilfreich markieren
  const markHilfreich = async (id) => {
    await fetch(`${BACKEND_URL}/api/wiki/eintraege/${id}/hilfreich`, { method: 'POST' });
    loadData();
  };

  // Regel bestätigen
  const toggleRegel = async (id, bestaetigt) => {
    await fetch(`${BACKEND_URL}/api/wiki/regeln/${id}/bestaetigen?bestaetigt=${!bestaetigt}`, { method: 'PATCH' });
    toast.success(bestaetigt ? 'Regel deaktiviert' : 'Regel aktiviert');
    loadData();
  };

  // Chat senden
  const sendChat = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput;
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setChatLoading(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/wiki/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nachricht: userMessage })
      });
      const data = await res.json();
      
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.antwort,
        wiki_vorschlag: data.wiki_vorschlag
      }]);
    } catch (e) {
      toast.error('Chat-Fehler');
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="wiki-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-slate-400 hover:text-slate-600">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-indigo-500" />
              Betreuungs-Wikipedia
            </h1>
            <p className="text-slate-500 text-sm">Wissensdatenbank & Lern-System</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setChatOpen(true)}>
            <MessageSquare className="w-4 h-4 mr-2" />
            KI-Chat
          </Button>
          <Button onClick={() => setEditDialog({ open: true, eintrag: null })}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Eintrag
          </Button>
        </div>
      </div>

      {/* Statistik-Karten */}
      {statistik && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 text-indigo-600 mb-1">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm">Wiki-Einträge</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{eintraege.length}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <Brain className="w-4 h-4" />
              <span className="text-sm">Gelernte Aktionen</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{statistik.total_aktionen}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Zap className="w-4 h-4" />
              <span className="text-sm">Auto-Regeln</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{statistik.total_regeln}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Bestätigt</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{statistik.bestaetigte_regeln}</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="wiki" className="space-y-4">
        <TabsList>
          <TabsTrigger value="wiki" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Wissensbasis
          </TabsTrigger>
          <TabsTrigger value="regeln" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Auto-Regeln ({regeln.length})
          </TabsTrigger>
          <TabsTrigger value="muster" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Erkannte Muster
          </TabsTrigger>
        </TabsList>

        {/* Wissensbasis Tab */}
        <TabsContent value="wiki" className="space-y-4">
          {/* Filter */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={selectedKategorie || 'alle'} onValueChange={(v) => setSelectedKategorie(v === 'alle' ? '' : v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Alle Kategorien" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Kategorien</SelectItem>
                {KATEGORIEN.map(k => (
                  <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Einträge Liste */}
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            </div>
          ) : eintraege.length > 0 ? (
            <div className="grid gap-4">
              {eintraege.map((eintrag) => {
                const kat = KATEGORIEN.find(k => k.value === eintrag.kategorie) || KATEGORIEN[0];
                return (
                  <div key={eintrag.id} className="bg-white rounded-lg border border-slate-200 hover:shadow-md transition-shadow overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 cursor-pointer" onClick={() => setExpandedId(expandedId === eintrag.id ? null : eintrag.id)}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={kat.color}>{kat.label}</Badge>
                            {eintrag.quelle === 'ki_vorschlag' && (
                              <Badge variant="outline" className="text-indigo-600">
                                <Sparkles className="w-3 h-3 mr-1" />
                                KI-Vorschlag
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-slate-800 mb-1">{eintrag.titel}</h3>
                          {expandedId !== eintrag.id && (
                            <p className="text-sm text-slate-600 line-clamp-2">{eintrag.inhalt?.replace(/[#*_`>-]/g, '').substring(0, 150)}</p>
                          )}
                          {eintrag.tags?.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {eintrag.tags.map(tag => (
                                <span key={tag} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 ml-4">
                          <Button size="sm" variant="ghost" onClick={() => setExpandedId(expandedId === eintrag.id ? null : eintrag.id)} className="text-slate-400">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => markHilfreich(eintrag.id)} className="text-slate-400 hover:text-green-500">
                            <ThumbsUp className="w-4 h-4" />
                            <span className="ml-1 text-xs">{eintrag.hilfreich_count || 0}</span>
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditDialog({ open: true, eintrag })}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteEintrag(eintrag.id)} className="text-slate-400 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    {expandedId === eintrag.id && (
                      <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-4" data-testid={`wiki-detail-${eintrag.id}`}>
                        <div className="prose prose-sm prose-slate max-w-none
                          prose-headings:text-slate-800 prose-headings:font-semibold
                          prose-p:text-slate-600 prose-p:leading-relaxed
                          prose-li:text-slate-600 prose-strong:text-slate-700
                          prose-code:bg-slate-200 prose-code:px-1 prose-code:rounded prose-code:text-xs">
                          <ReactMarkdown>{eintrag.inhalt}</ReactMarkdown>
                        </div>
                        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-200 text-xs text-slate-400">
                          {eintrag.erstellt_am && <span>Erstellt: {new Date(eintrag.erstellt_am).toLocaleDateString('de-DE')}</span>}
                          {eintrag.aktualisiert_am && <span>Aktualisiert: {new Date(eintrag.aktualisiert_am).toLocaleDateString('de-DE')}</span>}
                          {eintrag.erstellt_von && <span>Von: {eintrag.erstellt_von}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <BookOpen className="w-12 h-12 mx-auto mb-2" />
              <p>Noch keine Einträge vorhanden</p>
              <p className="text-sm mt-1">Erstellen Sie Ihren ersten Wissensbasis-Eintrag</p>
            </div>
          )}
        </TabsContent>

        {/* Auto-Regeln Tab */}
        <TabsContent value="regeln" className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-indigo-800">So funktioniert das Lern-System</p>
                <p className="text-sm text-indigo-600 mt-1">
                  Wenn Sie im Posteingang mehrmals die gleiche Aktion für einen Absender ausführen,
                  erkennt die KI das Muster und schlägt eine Automatisierung vor.
                  Bestätigte Regeln werden automatisch angewendet.
                </p>
              </div>
            </div>
          </div>

          {regeln.length > 0 ? (
            <div className="space-y-3">
              {regeln.map((regel) => (
                <div 
                  key={regel.id} 
                  className={`bg-white rounded-lg p-4 border transition-colors ${
                    regel.bestaetigt ? 'border-green-200 bg-green-50/50' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        regel.bestaetigt ? 'bg-green-100' : 'bg-slate-100'
                      }`}>
                        <Zap className={`w-5 h-5 ${regel.bestaetigt ? 'text-green-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">
                          Wenn <span className="text-indigo-600">{regel.bedingung_typ}</span> = "{regel.bedingung_wert}"
                        </p>
                        <p className="text-sm text-slate-500">
                          → <span className="font-medium">{regel.aktion}</span>
                          {regel.ausfuehrungen > 0 && ` (${regel.ausfuehrungen}x ausgeführt)`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={regel.bestaetigt ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                        {regel.bestaetigt ? 'Aktiv' : 'Vorschlag'}
                      </Badge>
                      <Button
                        size="sm"
                        variant={regel.bestaetigt ? 'outline' : 'default'}
                        onClick={() => toggleRegel(regel.id, regel.bestaetigt)}
                      >
                        {regel.bestaetigt ? 'Deaktivieren' : 'Aktivieren'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Zap className="w-12 h-12 mx-auto mb-2" />
              <p>Noch keine Regeln vorhanden</p>
              <p className="text-sm mt-1">Die KI lernt aus Ihren Aktionen im Posteingang</p>
            </div>
          )}
        </TabsContent>

        {/* Erkannte Muster Tab */}
        <TabsContent value="muster" className="space-y-4">
          {statistik?.haeufige_muster?.length > 0 ? (
            <div className="space-y-3">
              {statistik.haeufige_muster.map((muster, i) => (
                <div key={i} className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-800">
                        {muster.absender || 'Unbekannt'} → {muster.aktion}
                      </p>
                      <p className="text-sm text-slate-500">{muster.anzahl}x beobachtet</p>
                    </div>
                    <Badge variant="outline">{muster.anzahl} Aktionen</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <BarChart3 className="w-12 h-12 mx-auto mb-2" />
              <p>Noch keine Muster erkannt</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Inline Edit-Bereich (kein Modal) */}
      {editDialog.open && (
        <Card className="max-w-2xl mx-auto mt-4 border-blue-200 shadow-lg" data-testid="wiki-edit-inline">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{editDialog.eintrag ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setEditDialog({ open: false, eintrag: null })}><X className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <EintragForm 
              eintrag={editDialog.eintrag} 
              onSave={saveEintrag}
              onCancel={() => setEditDialog({ open: false, eintrag: null })}
            />
          </CardContent>
        </Card>
      )}

      {/* Inline Chat-Bereich (kein Modal) */}
      {chatOpen && (
        <Card className="max-w-2xl mx-auto mt-4 border-indigo-200 shadow-lg" data-testid="wiki-chat-inline">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="w-5 h-5 text-indigo-500" />
                KI-Assistent für Betreuungswissen
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setChatOpen(false)}><X className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[400px] overflow-y-auto space-y-4 p-4 bg-slate-50 rounded-lg">
              {chatMessages.length === 0 ? (
                <div className="text-center text-slate-400 py-8">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2" />
                  <p>Fragen Sie mich zu Betreuungsthemen</p>
                  <p className="text-sm">Ich lerne aus unseren Gesprächen</p>
                </div>
              ) : (
                chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      msg.role === 'user' ? 'bg-indigo-500 text-white' : 'bg-white border border-slate-200'
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      {msg.wiki_vorschlag && (
                        <div className="mt-2 p-2 bg-indigo-50 rounded border border-indigo-200">
                          <p className="text-sm text-indigo-700">{msg.wiki_vorschlag.nachricht}</p>
                          <Button size="sm" className="mt-2" variant="outline">
                            <Plus className="w-3 h-3 mr-1" />
                            In Wiki aufnehmen
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border rounded-lg p-3">
                    <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-4">
              <Input
                placeholder="Frage stellen..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendChat()}
              />
              <Button onClick={sendChat} disabled={chatLoading}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Eintrag Formular Komponente
function EintragForm({ eintrag, onSave, onCancel }) {
  const [form, setForm] = useState({
    titel: eintrag?.titel || '',
    inhalt: eintrag?.inhalt || '',
    kategorie: eintrag?.kategorie || 'allgemein',
    tags: eintrag?.tags?.join(', ') || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      id: eintrag?.id,
      titel: form.titel,
      inhalt: form.inhalt,
      kategorie: form.kategorie,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      quelle: eintrag?.quelle || 'manuell'
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Titel</Label>
        <Input
          value={form.titel}
          onChange={(e) => setForm(f => ({ ...f, titel: e.target.value }))}
          placeholder="z.B. Vorgehen bei AOK-Bescheid"
          required
        />
      </div>
      
      <div>
        <Label>Kategorie</Label>
        <Select value={form.kategorie} onValueChange={(v) => setForm(f => ({ ...f, kategorie: v }))}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KATEGORIEN.map(k => (
              <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>Inhalt</Label>
        <Textarea
          value={form.inhalt}
          onChange={(e) => setForm(f => ({ ...f, inhalt: e.target.value }))}
          placeholder="Beschreiben Sie das Wissen..."
          rows={6}
          required
        />
      </div>
      
      <div>
        <Label>Tags (kommagetrennt)</Label>
        <Input
          value={form.tags}
          onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
          placeholder="AOK, Krankenkasse, Hilfsmittel"
        />
      </div>
      
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>Abbrechen</Button>
        <Button type="submit">Speichern</Button>
      </div>
    </form>
  );
}
